import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { VehicleGallery } from '@/components/VehicleGallery';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Car, 
  ArrowLeft, 
  Loader2, 
  User,
  Calendar,
  FileText,
  Trophy,
  Vote,
  ChevronLeft,
  ChevronRight,
  Share2,
  ThumbsUp,
  Hash,
  ExternalLink,
  ShoppingBag
} from 'lucide-react';
import { Lightbox } from '@/components/Lightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VehicleDetailPage() {
  const { memberId, vehicleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [owner, setOwner] = useState(null);
  const [allVehicles, setAllVehicles] = useState([]);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [sellingListing, setSellingListing] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');
  const currentUserId = (() => { try { const payload = JSON.parse(atob(getToken().split('.')[1])); return payload.sub || payload.id || ''; } catch { return ''; } })();

  useEffect(() => {
    fetchVehicleDetails();
    checkVotingStatus();
    fetchLikeStatus();
  }, [memberId, vehicleId]);

  const fetchVehicleDetails = async () => {
    try {
      const token = getToken();
      
      // Fetch member details including vehicles
      const memberResponse = await axios.get(
        `${API_URL}/api/members/${memberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const memberData = memberResponse.data;
      setOwner({
        id: memberData.id,
        name: memberData.name,
        email: memberData.email
      });
      
      // Find the specific vehicle
      const vehicles = memberData.vehicles || [];
      const foundVehicle = vehicles.find(v => v.id === vehicleId);
      
      if (foundVehicle) {
        setVehicle(foundVehicle);
        // Filter only public vehicles for navigation
        setAllVehicles(vehicles.filter(v => v.is_public !== false));
      } else {
        toast.error('Fahrzeug nicht gefunden');
        navigate('/member-vehicles');
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Fehler beim Laden des Fahrzeugs');
    } finally {
      setLoading(false);
    }
  };

  const checkVotingStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/voting/car-of-the-month`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const userVote = response.data.user_vote;
      if (userVote && userVote.vehicle_id === vehicleId && userVote.member_id === memberId) {
        setHasVoted(true);
      }
    } catch (error) {
      console.error('Error checking voting status:', error);
    }
  };

  const fetchLikeStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/vehicles/likes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikeCount((response.data.counts || {})[vehicleId] || 0);
      setLiked((response.data.my_likes || []).includes(vehicleId));
    } catch (error) { /* not critical */ }
  };

  const toggleLike = async () => {
    try {
      const token = getToken();
      const response = await axios.post(`${API_URL}/api/vehicles/${vehicleId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(response.data.liked);
      setLikeCount(response.data.count);
    } catch (error) {
      toast.error('Fehler beim Liken');
    }
  };

  const handleVote = async () => {
    setVoting(true);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/api/voting/car-of-the-month/vote`,
        { vehicle_id: vehicleId, member_id: memberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stimme abgegeben!');
      setHasVoted(true);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Abstimmen';
      toast.error(msg);
    } finally {
      setVoting(false);
    }
  };

  const navigateToVehicle = (direction) => {
    const currentIndex = allVehicles.findIndex(v => v.id === vehicleId);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allVehicles.length - 1;
    } else {
      newIndex = currentIndex < allVehicles.length - 1 ? currentIndex + 1 : 0;
    }
    
    navigate(`/fahrzeug/${memberId}/${allVehicles[newIndex].id}`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: vehicle?.name,
        text: `${vehicle?.name} - ${owner?.name}`,
        url: window.location.href
      }).catch((err) => {
        // User cancelled sharing - don't show error
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!vehicle) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Car className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Fahrzeug nicht gefunden</p>
          <Link to="/member-vehicles">
            <Button variant="link" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Übersicht
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const images = vehicle.images || [];
  const currentVehicleIndex = allVehicles.findIndex(v => v.id === vehicleId);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb & Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link 
            to="/member-vehicles"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Fahrzeuge
          </Link>
          
          {/* Vehicle Navigation within owner's collection */}
          {allVehicles.length > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateToVehicle('prev')}
                className="rounded-none"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {currentVehicleIndex + 1} / {allVehicles.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateToVehicle('next')}
                className="rounded-none"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Image Gallery - Takes 2 columns on large screens */}
          <div className="lg:col-span-2">
            {images.length > 0 ? (
              <>
                {/* Main Image */}
                <div 
                  className="aspect-[16/10] bg-muted/30 border border-border overflow-hidden mb-4 relative group cursor-pointer"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="vehicle-main-image"
                >
                  <img
                    src={images[currentImageIndex]?.url}
                    alt={vehicle.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Image Navigation */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(i => i > 0 ? i - 1 : images.length - 1)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(i => i < images.length - 1 ? i + 1 : 0)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      
                      {/* Image Counter */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full">
                        <span className="text-white text-sm">
                          {currentImageIndex + 1} / {images.length}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Thumbnail Strip */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                      <button
                        key={img.id || idx}
                        onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                        className={`flex-shrink-0 w-20 h-20 border-2 overflow-hidden transition-all ${
                          idx === currentImageIndex 
                            ? 'border-primary' 
                            : 'border-transparent hover:border-primary/50'
                        }`}
                      >
                        <img
                          src={img.url}
                          alt={`${vehicle.name} ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="aspect-[16/10] bg-muted/30 border border-border flex items-center justify-center">
                <Car className="w-24 h-24 text-muted-foreground/30" />
              </div>
            )}

            {/* Detailed Description - Below Images */}
            {vehicle.detailed_description && (
              <div className="bg-card border border-border p-6 mt-6">
                <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wide text-foreground mb-4">
                  Detailbeschreibung
                </h2>
                <div 
                  className="prose prose-sm max-w-none text-muted-foreground
                    prose-headings:font-['Oswald'] prose-headings:text-foreground prose-headings:uppercase
                    prose-h2:text-lg prose-h3:text-base
                    prose-p:leading-relaxed prose-p:mb-4
                    prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5
                    prose-li:mb-1
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-foreground
                    prose-hr:border-border"
                  dangerouslySetInnerHTML={{ __html: vehicle.detailed_description }}
                />
              </div>
            )}
          </div>

          {/* Info Sidebar */}
          <div className="space-y-6">
            {/* Vehicle Title */}
            <div>
              <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide text-foreground">
                {vehicle.name}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                {vehicle.year && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="text-xl text-primary font-medium">{vehicle.year}</span>
                  </div>
                )}
                <button
                  onClick={toggleLike}
                  className={`flex items-center gap-2 px-3 py-1.5 border transition-all ${liked ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary'}`}
                  data-testid="vehicle-like-btn"
                >
                  <ThumbsUp size={18} className={liked ? 'fill-primary' : ''} />
                  <span className="text-sm font-medium">{likeCount}</span>
                </button>
              </div>
            </div>

            {/* VIN/Serial Number */}
            {vehicle.vin && (
              <div className="bg-card border border-border p-4">
                <h2 className="font-medium text-foreground mb-2 flex items-center gap-2">
                  <Hash className="w-4 h-4 text-primary" />
                  Fahrgestellnummer (VIN/S/N)
                </h2>
                <p className="font-mono text-muted-foreground">
                  {vehicle.vin}
                </p>
              </div>
            )}

            {/* External Link */}
            {vehicle.external_link && (
              <a 
                href={vehicle.external_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <ExternalLink className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Mehr Infos zum Fahrzeug</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">{vehicle.external_link}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </a>
            )}

            {/* Sell on Marketplace */}
            {currentUserId === memberId && (
              <button
                onClick={async () => {
                  if (sellingListing) return;
                  setSellingListing(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/marketplace/listings/from-vehicle/${vehicleId}`, {}, {
                      headers: { Authorization: `Bearer ${getToken()}` }
                    });
                    toast.success('Verkaufsinserat erstellt!');
                    navigate(`/marketplace/${res.data.listing.id}/edit`);
                  } catch { toast.error('Fehler beim Erstellen'); setSellingListing(false); }
                }}
                disabled={sellingListing}
                className="flex items-center justify-between w-full p-4 bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 transition-colors group"
                data-testid="sell-vehicle-btn"
              >
                <div className="flex items-center gap-3">
                  <ShoppingBag className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-foreground">Zum Verkauf anbieten</span>
                </div>
                <ChevronRight className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
              </button>
            )}

            {/* Description */}
            {vehicle.description && (
              <div className="bg-card border border-border p-4">
                <h2 className="font-medium text-foreground mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Kurzbeschreibung
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {vehicle.description}
                </p>
              </div>
            )}

            {/* Owner */}
            <div className="bg-card border border-border p-4">
              <h2 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                Besitzer
              </h2>
              <Link 
                to={`/members/${owner?.id}`}
                className="flex items-center gap-3 hover:bg-muted/50 p-2 -m-2 transition-colors"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{owner?.name}</p>
                  <p className="text-sm text-muted-foreground">Profil ansehen →</p>
                </div>
              </Link>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {/* Vote Button */}
              {images.length > 0 && (
                <Button
                  onClick={handleVote}
                  disabled={voting || hasVoted}
                  className="w-full rounded-none uppercase tracking-wider"
                  variant={hasVoted ? "outline" : "default"}
                >
                  {voting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : hasVoted ? (
                    <>
                      <ThumbsUp className="w-4 h-4 mr-2 fill-current" />
                      Ihre Stimme
                    </>
                  ) : (
                    <>
                      <Vote className="w-4 h-4 mr-2" />
                      Für "Auto des Monats" stimmen
                    </>
                  )}
                </Button>
              )}
              
              {/* Share Button */}
              <Button
                onClick={handleShare}
                variant="outline"
                className="w-full rounded-none"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Teilen
              </Button>
              
              {/* Link to voting page */}
              <Link to="/car-of-the-month" className="block">
                <Button variant="ghost" className="w-full rounded-none">
                  <Trophy className="w-4 h-4 mr-2" />
                  Zur Abstimmung
                </Button>
              </Link>
            </div>

            {/* Other vehicles from same owner */}
            {allVehicles.length > 1 && (
              <div className="bg-card border border-border p-4">
                <h2 className="font-medium text-foreground mb-3">
                  Weitere Fahrzeuge von {owner?.name}
                </h2>
                <div className="space-y-2">
                  {allVehicles
                    .filter(v => v.id !== vehicleId)
                    .slice(0, 4)
                    .map(v => (
                      <Link
                        key={v.id}
                        to={`/fahrzeug/${memberId}/${v.id}`}
                        className="flex items-center gap-3 p-2 hover:bg-muted/50 transition-colors -mx-2"
                      >
                        {v.images && v.images[0] ? (
                          <img
                            src={v.images[0].url}
                            alt={v.name}
                            className="w-12 h-12 object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted/30 flex items-center justify-center">
                            <Car className="w-6 h-6 text-muted-foreground/50" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{v.name}</p>
                          {v.year && (
                            <p className="text-xs text-muted-foreground">{v.year}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <Lightbox
          images={images}
          initialIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </Layout>
  );
}
